import { AbstractShape } from "../abstract/Shape";
import { clearConnectionFocus, clearFocus } from "../utils/ListUtils";
import { Camera } from "./Camera";
import { Connection } from "./Connection";
import { Values } from "./Values";
import { ConnectionRef } from "./ConnectionRef";
import { ShapeRefArray } from "./ShapeRefArray";
import { CanvasRenderer } from "../abstract/CanvasRenderer";
import { ShapeRef } from "./ShapeRef";

export class CanvasHelper {

    camera!: Camera;
    // draw: Function;
    isResizing: boolean = false;
    // mouseOffset: Values = new Values(0, 0);
    canvasRenderer: CanvasRenderer;
    currentDropIn: AbstractShape | undefined;
    selectedShapesOffsets: Map<AbstractShape, Values> = new Map();

    private _prevClickShape: AbstractShape | null = null;
    private _lastClickShape: AbstractShape | null = null;

    constructor(camera: Camera, canvasRenderer: CanvasRenderer) {
        this.camera = camera;
        this.canvasRenderer = canvasRenderer;
    }

    checkCameraPanning(e: MouseEvent): boolean {
        if (e.button === 2) {
            // If no box is clicked, enable camera panning
            this.camera.isPanning = true;
            this.camera.lastMouseX = e.offsetX;
            this.camera.lastMouseY = e.offsetY;
            this.canvasRenderer.draw();
            e.preventDefault();
            return true;
        }
        return false;
    }

    checkSelectedConnection(
        e: MouseEvent,
        connections: Connection[],
        canvasItems: AbstractShape[],
        currentConnectionFocus: ConnectionRef,
        currentFocus: ShapeRefArray,
        setConnectionList: Function
    ): boolean {
        const { x, y } = this.camera.toCanvasCoordinates(e.offsetX, e.offsetY);
        const connection = connections.find((connection) =>
            connection.isMouseOverConnection(x, y)
        );

        if (connection) {

            let index = connections.findIndex((item: Connection) => item.id === connection.id);
            let maxItems = connections.length - 1;
            if (index != maxItems) {
                let filtered = connections.filter((item: Connection) => item.id != connection.id);
                setConnectionList([...filtered, connection]);
            }

            currentConnectionFocus.setConnection(connection);
            clearConnectionFocus(connections);
            connection.setFocus();

            currentFocus.resetList();
            clearFocus(canvasItems);
            this.canvasRenderer.draw();
            return true;
        }
        return false;
    }

    checkSelectedShape(
        e: MouseEvent,
        connections: Connection[],
        canvasItems: AbstractShape[],
        currentConnectionFocus: ConnectionRef,
        currentFocus: ShapeRefArray,
        setShapesList: Function
    ) {
        const { x, y } = this.camera.toCanvasCoordinates(e.offsetX, e.offsetY);
        let selected = this.selectCurrentChild(canvasItems, new Values(x, y));
        if (selected) {

            this._prevClickShape = this._lastClickShape;
            this._lastClickShape = selected;
            
            if (this.canvasRenderer.isCtrl.value) {
                // Toggle selection
                if (currentFocus.getShape().includes(selected)) {
                    currentFocus.removeShape(selected);
                    selected.setFocus(false);
                } else {
                    currentFocus.addShape(selected);
                    selected.setFocus(true);
                }
            } else if(!currentFocus.getShape().some(shape => shape===selected)){
                // Single selection
                currentFocus.getShape().forEach(s => s.setFocus(false));
                currentFocus.setShapes([selected]);
                selected.setFocus(true);

                let parent = selected.parent;
                if (parent) {
                    let index = parent.children.findIndex((item: AbstractShape) => item.id === selected.id);
                    let maxItems = parent.children.length - 1;
                    if (index != maxItems) {
                        let filtered = parent.children.filter((item: AbstractShape) => item.id != selected.id);
                        parent.children = [...filtered, selected];
                    }
                }else{
                    let index = canvasItems.findIndex((item: AbstractShape) => item.id === selected.id);
                    let maxItems = canvasItems.length - 1;
                    if (index != maxItems) {
                        let filtered = canvasItems.filter((item: AbstractShape) => item.id != selected.id);
                        setShapesList([...filtered, selected]);
                    }
                }
            }
            // Prepare for group drag
            this.selectedShapesOffsets.clear();
            this.isResizing = selected.shouldResize(new Values(x, y), canvasItems);

            if(!this.isResizing){
                currentFocus.getShape().forEach(shape => {
                    this.selectedShapesOffsets.set(shape, new Values(shape.x - x, shape.y - y));
                    shape.isDragging = true;
                });
            }
            currentConnectionFocus.setConnection(null);
            clearConnectionFocus(connections);
            this.canvasRenderer.draw();
            return true;
        }
        return false;
    }

    selectCurrentChild(childs: AbstractShape[], point: Values, parent?: AbstractShape): AbstractShape | undefined {
        if (parent) {
            point = new Values(point.x - parent.x, point.y - parent.y);
        }
        let reversed = [...childs].reverse();
        for (const item of reversed) {
            if (item.containsPoint(point.x, point.y)) {
                if (item.fill) return item;
                let child = this.selectCurrentChild(item.children, point, item);
                if (child) return child;
                return item;
            }
        }
        return undefined;
    }

    selectSecondChild(childs: AbstractShape[], point: Values, parent?: AbstractShape): AbstractShape | undefined {
        if (parent) {
            point = new Values(point.x - parent.x, point.y - parent.y);
        }
        let reversed = [...childs].reverse();
        for (const item of reversed) {
            if (item.isDragging) continue;
            if (item.containsPoint(point.x, point.y)) {
                if (item.fill) return item;
                let child = this.selectSecondChild(item.children, point, item);
                if (child) return child;
                return item;
            }
        }
        return undefined;
    }

    onHoverShape(
        e: MouseEvent,
        canvasItems: AbstractShape[],
        hoveredShape: ShapeRef
    ): boolean {
        const { offsetX, offsetY } = e;
        const { x, y } = this.camera.toCanvasCoordinates(offsetX, offsetY);
        let hoverOverShape = this.selectCurrentChild(canvasItems, new Values(x, y));

        if (hoverOverShape) {
            if (hoverOverShape !== hoveredShape.getShape()) {
                if (hoveredShape.getShape()) hoveredShape.getShape()!.setHover(false);
                hoveredShape.setShape(hoverOverShape);
                hoveredShape.getShape()!.setHover(true);
            }
            return true;
        } else {
            if (hoveredShape.getShape()) hoveredShape.getShape()!.setHover(false);
            hoveredShape.setShape(null);
            return false;
        }
    }

    onHoverConnection(
        e: MouseEvent,
        connections: Connection[],
        hoveredConnection: ConnectionRef
    ) {
        const { offsetX, offsetY } = e;
        const { x, y } = this.camera.toCanvasCoordinates(offsetX, offsetY);
        const hovered = connections.find((connection) =>
            connection.isMouseOverConnection(x, y)
        );

        if (hovered) {
            if (hovered !== hoveredConnection.getConnection()) {
                if (hoveredConnection.getConnection()) hoveredConnection.getConnection()!.setHover(false);
                hoveredConnection.setConnection(hovered);
                hoveredConnection.getConnection()!.setHover(true);
            }
            return true;
        } else {
            if (hoveredConnection.getConnection()) hoveredConnection.getConnection()!.setHover(false);
            hoveredConnection.setConnection(null);
            return false;
        }
    }

    paneCamera(e: MouseEvent) {
        if (this.camera.isPanning) {
            this.camera.cameraPanning(e, () => this.canvasRenderer.draw());
            return;
        }
    }

    resizeOrMoveShape(
        e: MouseEvent,
        canvasItems: AbstractShape[],
        currentFocus: ShapeRefArray,
    ) {
        const { offsetX, offsetY } = e;
        const { x, y } = this.camera.toCanvasCoordinates(offsetX, offsetY);
        let focusList = currentFocus.getShape();
        for (const focus of focusList) {
            if (focus) {            
                if (this.isResizing) {
                    const offset = focus.calculateAllOffsets(canvasItems);

                    let newWidth = x - focus.x - offset.x;
                    let newHeight = y - focus.y - offset.y;

                    // Constraint 1: Respect children bounds
                    const childBounds = focus.getChildrenBounds();
                    let largest = Math.max(focus.width, focus.height);
                    let fatherWidth = focus.width;
                    let fatherHeight = focus.height;
                    const minWidth = Math.max(20, childBounds.x + childBounds.width + fatherWidth / ((fatherWidth===largest) ? 65 : 40));
                    const minHeight = Math.max(20, childBounds.y + childBounds.height + fatherHeight / ((fatherHeight===largest) ? 65 : 40));

                    // Constraint 2: Respect parent bounds (if any)
                    let maxWidth = Infinity;
                    let maxHeight = Infinity;

                    if (focus.parent) {
                        const parentWidth = focus.parent.width;
                        const parentHeight = focus.parent.height;
                        const parentOffsetX = focus.x;
                        const parentOffsetY = focus.y;

                        maxWidth = parentWidth - parentOffsetX;
                        maxHeight = parentHeight - parentOffsetY;
                    }

                    // Clamp width and height
                    const width = Math.max(minWidth, Math.min(newWidth, maxWidth));
                    const height = Math.max(minHeight, Math.min(newHeight, maxHeight));

                    focus.setSize(width, height);

                } else if (focus.isDragging) {
                    // let parent = focus.parent;
                    // if(parent){
                    //     let index = parent.children.findIndex((item: AbstractShape)=>item.id === focus.id);
                    //     let maxItems = parent.children.length-1;
                    //     if(index != maxItems){
                    //         let filtered = parent.children.filter((item: AbstractShape)=>item.id != focus.id);
                    //         parent.children = [...filtered, focus];
                    //     }
                    // }
                    this.moveItem(focus, x, y);
                    if(focusList.length === 1){
                        let otherShape = this.selectSecondChild(canvasItems, new Values(x, y));
                        if(otherShape === focus || otherShape === focus.parent) {
                            if(this.currentDropIn) this.currentDropIn.setFocus(false);
                            this.currentDropIn = undefined;
                            return;
                        }
                        if(otherShape){
                            if (this.currentDropIn) {
                                if (this.currentDropIn != otherShape) this.currentDropIn.setFocus(false);
                            }
                            this.currentDropIn = otherShape;
                            this.currentDropIn.setFocus(true);

                        }else{
                            if(this.currentDropIn) this.currentDropIn.setFocus(false);
                            this.currentDropIn = undefined;
                        }
                    }
                }
            }
        }
    }

    moveItem(item: AbstractShape, x: number, y: number){
        const dx = (x - item.x) + this.selectedShapesOffsets.get(item)!.x || 0;
        const dy = (y - item.y) + this.selectedShapesOffsets.get(item)!.y || 0;
        item.move(dx, dy);
    }

    removeDraggingOrResizing(
        currentFocus: ShapeRefArray
    ) {
        let focusList = currentFocus.getShape();

        if (focusList) {
            for (const focus of focusList) {
                focus.isDragging = false;
            }
        }
        this.isResizing = false;
    }

    removeFocus(
        currentFocus: ShapeRefArray,
        currentConnectionFocus: ConnectionRef,
        connections: Connection[],
        canvasItems: AbstractShape[],
    ){
        currentFocus.resetList();
        currentConnectionFocus.setConnection(null);
        clearConnectionFocus(connections);
        clearFocus(canvasItems);
    }

    stopCameraPanning(){
        this.camera.isPanning = false;
    }

    toggleFill(){
        if (
            this._lastClickShape &&
            this._prevClickShape &&
            this._lastClickShape === this._prevClickShape
        ) {
            this._lastClickShape.toggleFill();
        }
    }

}